import { useEffect, useRef, useCallback, useState } from 'react';
import { cacheGameState, getCachedGameState, clearGameCache } from '../utils/gameSync';

/**
 * useHostModeProtection - Comprehensive crash protection for Host Mode
 * 
 * Features:
 * - Navigation guards (beforeunload, popstate)
 * - Auto-save with timestamps
 * - Resume detection and modal
 * - Fullscreen controls
 * - Status banner with save info
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.gameId - Current game ID
 * @param {Object} options.gameState - Current game state to save
 * @param {boolean} options.isGameActive - Whether game is currently running
 * @param {Function} options.onResume - Callback when user chooses to resume
 * @param {Function} options.onDiscard - Callback when user chooses to discard save
 * @param {number} options.autoSaveInterval - Auto-save interval in ms (default: 5000)
 * @param {boolean} options.enableFullscreen - Whether to enable fullscreen controls
 * @returns {Object} Protection state and controls
 */
export const useHostModeProtection = ({
  gameId,
  gameState,
  isGameActive = false,
  onResume,
  onDiscard,
  autoSaveInterval = 5000,
  enableFullscreen = true
}) => {
  const autoSaveRef = useRef(null);
  const lastSaveRef = useRef(null);
  const lastSaveStateRef = useRef(null);
  const hasUnsavedChangesRef = useRef(false);
  const isNavigatingRef = useRef(false);
  const fullscreenRef = useRef(false);
  const isResumingRef = useRef(false);

  // State for UI
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showSaveExitModal, setShowSaveExitModal] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isCheckingSave, setIsCheckingSave] = useState(false);
  const cachedSaveRef = useRef(null);

  // Store gameState in ref to avoid dependency issues
  const gameStateRef = useRef(gameState);
  gameStateRef.current = gameState;

  // Check localStorage availability and quota
  const checkStorageHealth = useCallback(() => {
    try {
      // Test if localStorage is available
      const testKey = `storage_test_${Date.now()}`;
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      
      // Estimate current usage
      let totalSize = 0;
      const keys = Object.keys(localStorage);
      for (const key of keys) {
        totalSize += localStorage.getItem(key)?.length || 0;
      }
      
      // Convert to MB
      const usageMB = totalSize / (1024 * 1024);
      
      // Warn if approaching limits
      if (usageMB > 4) {
        console.warn('⚠️ STORAGE: Approaching localStorage limit:', usageMB.toFixed(2), 'MB');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('❌ STORAGE: localStorage not available:', error);
      return false;
    }
  }, []);

  // Optimized auto-save function with storage health checks
  const saveGameState = useCallback(() => {
    if (!gameId || !gameStateRef.current || !isGameActive) return;

    // Prevent saves during save check or while resume modal is showing
    if (isCheckingSave || showResumeModal) {
      console.log('⏸️ SAVE: Blocked to protect existing save data', { 
        isCheckingSave, 
        showResumeModal 
      });
      return;
    }

    // Check storage health before saving
    if (!checkStorageHealth()) {
      console.warn('⚠️ AUTO-SAVE: Skipping save due to storage issues');
      setSaveStatus('error');
      return;
    }

    try {
      setSaveStatus('saving');
      
      // Only save essential data, not the full game state
      const essentialData = {
        teams: gameStateRef.current.teams?.map(team => ({
          id: team.id,
          name: team.name,
          score: team.score,
          gpa: team.gpa,
          usedPoints: team.usedPoints || {},
          correctAnswers: team.correctAnswers || {},
          wrongAnswers: team.wrongAnswers || {}
        })) || [],
        currentStage: gameStateRef.current.currentStage,
        revealAnswer: gameStateRef.current.revealAnswer,
        gameComplete: gameStateRef.current.gameComplete,
        currentSlideIndex: gameStateRef.current.currentSlideIndex,
        lastSaved: new Date().toISOString(),
        gameId,
        saveVersion: '1.0'
      };

      // Save directly - overwrites any existing save for this game
      cacheGameState(gameId, essentialData);
      
      lastSaveRef.current = new Date();
      lastSaveStateRef.current = {
        stage: essentialData.currentStage,
        teamsCount: essentialData.teams.length,
        teamScores: essentialData.teams.map(t => t.score)
      };
      setLastSaveTime(lastSaveRef.current);
      setSaveStatus('saved');
      hasUnsavedChangesRef.current = false;

      // Simple save confirmation logging
      console.log('💾 SINGLE-SAVE: Game state saved (overwrites previous)', {
        gameId,
        timestamp: lastSaveRef.current.toISOString(),
        teamsCount: essentialData.teams.length,
        stage: essentialData.currentStage,
        storageKey: `gameState_${gameId}`
      });

      // Clear saved status after 2 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);

    } catch (error) {
      console.error('❌ AUTO-SAVE: Failed to save game state:', error);
      setSaveStatus('error');
      
      // Retry after 5 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 5000);
    }
  }, [gameId, isGameActive, checkStorageHealth, isCheckingSave, showResumeModal]);

  // Detect device performance and adjust save strategy
  const detectDevicePerformance = useCallback(() => {
    try {
      // Check available memory (if supported)
      if ('memory' in performance) {
        const memory = performance.memory;
        const usedMB = memory.usedJSHeapSize / (1024 * 1024);
        const totalMB = memory.totalJSHeapSize / (1024 * 1024);
        
        // If using more than 80% of available memory, reduce save frequency
        if (usedMB / totalMB > 0.8) {
          console.warn('⚠️ PERFORMANCE: High memory usage detected, reducing save frequency');
          return 'low';
        }
      }
      
      // Check if device is mobile (generally have less RAM)
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobile) {
        console.log('📱 DEVICE: Mobile device detected, using conservative save strategy');
        return 'mobile';
      }
      
      return 'normal';
    } catch (error) {
      console.warn('⚠️ PERFORMANCE: Could not detect device performance, using conservative strategy');
      return 'low';
    }
  }, []);

  // Check for existing save on mount
  const checkForExistingSave = useCallback(() => {
    if (!gameId) return;

    try {
      // First check if localStorage is available
      if (!checkStorageHealth()) {
        console.warn('⚠️ STORAGE: localStorage not available, skipping save check');
        return;
      }

      setIsCheckingSave(true);
      
      const existingSave = getCachedGameState(gameId);
      const rawStorageCheck = localStorage.getItem(`gameState_${gameId}`);
      console.log('🔍 SINGLE-SAVE: Checking for save', { 
        gameId, 
        hasSave: !!existingSave,
        saveData: existingSave,
        rawStorageData: rawStorageCheck
      });
      
      // Check if save data is inconsistent
      if (existingSave && rawStorageCheck) {
        const rawParsed = JSON.parse(rawStorageCheck);
        if (existingSave.teams?.length !== rawParsed.teams?.length || existingSave.currentStage !== rawParsed.currentStage) {
          console.error('🚨 SAVE DATA MISMATCH!', {
            fromGetCached: { teams: existingSave.teams?.length, stage: existingSave.currentStage },
            fromRawStorage: { teams: rawParsed.teams?.length, stage: rawParsed.currentStage },
            parsedSave: existingSave,
            rawData: rawParsed
          });
        }
      }
      
      if (existingSave && existingSave.lastSaved) {
        const saveTime = new Date(existingSave.lastSaved);
        const now = new Date();
        const hoursSinceSave = (now - saveTime) / (1000 * 60 * 60);

        // Show resume modal if save is less than 24 hours old
        if (hoursSinceSave < 24) {
          // Cache the save data so it can't be overwritten
          cachedSaveRef.current = existingSave;
          setShowResumeModal(true);
          console.log('🔄 RESUME: Found existing save', {
            gameId,
            saveTime: saveTime.toISOString(),
            hoursSinceSave: hoursSinceSave.toFixed(2),
            fullSaveData: existingSave
          });
        } else {
          // Clear old save
          clearGameCache(gameId);
          console.log('🗑️ CLEARED: Old save (>24h) removed');
        }
      } else {
        console.log('❌ NO SAVE: No existing save found for game', gameId);
      }
      
      // Always clear the checking flag after save check completes
      setTimeout(() => setIsCheckingSave(false), 1000);
    } catch (error) {
      console.warn('⚠️ STORAGE: Could not check for existing save:', error);
      setIsCheckingSave(false);
      // Continue without save functionality - game will work normally
    }
  }, [gameId, checkStorageHealth]);

  // Navigation guard for beforeunload (tab close, refresh)
  const handleBeforeUnload = useCallback((event) => {
    if (!isGameActive || isNavigatingRef.current) return;

    if (hasUnsavedChangesRef.current) {
      const message = '⚠️ You have unsaved changes. Are you sure you want to leave?';
      event.preventDefault();
      event.returnValue = message;
      return message;
    }
  }, [isGameActive]);

  // Navigation guard for popstate (back button)
  const handlePopState = useCallback((event) => {
    if (!isGameActive || isNavigatingRef.current) return;

    if (hasUnsavedChangesRef.current) {
      event.preventDefault();
      setShowLeaveModal(true);
      
      // Push current state back to prevent navigation
      window.history.pushState(null, '', window.location.href);
    }
  }, [isGameActive]);

  // Handle resume game
  const handleResume = useCallback(() => {
    setShowResumeModal(false);
    isResumingRef.current = true; // Prevent critical saves during restoration
    
    if (onResume) {
      onResume();
    }
    // Start auto-save after resume is complete
    console.log('🔄 RESUME COMPLETE: Starting auto-save now');
    setTimeout(() => {
      isResumingRef.current = false; // Allow critical saves again
      if (!autoSaveRef.current && isGameActive) {
        const performanceLevel = detectDevicePerformance();
        let actualSaveInterval = autoSaveInterval;
        if (performanceLevel === 'low' || performanceLevel === 'mobile') {
          actualSaveInterval = Math.max(autoSaveInterval * 2, 30000);
        }
        
        autoSaveRef.current = setInterval(() => {
          saveGameState();
        }, actualSaveInterval);
        console.log('✅ AUTO-SAVE: Started after resume');
      }
    }, 2000); // Give more time for state restoration to complete
  }, [onResume, isGameActive, autoSaveInterval, detectDevicePerformance, saveGameState]);

  // Handle discard save
  const handleDiscard = useCallback(() => {
    setShowResumeModal(false);
    if (gameId) {
      clearGameCache(gameId);
    }
    if (onDiscard) {
      onDiscard();
    }
    // Start auto-save after discard is complete
    console.log('🗑️ DISCARD COMPLETE: Starting auto-save for new game');
    setTimeout(() => {
      if (!autoSaveRef.current && isGameActive) {
        const performanceLevel = detectDevicePerformance();
        let actualSaveInterval = autoSaveInterval;
        if (performanceLevel === 'low' || performanceLevel === 'mobile') {
          actualSaveInterval = Math.max(autoSaveInterval * 2, 30000);
        }
        
        // Do initial save for new game
        saveGameState();
        
        autoSaveRef.current = setInterval(() => {
          saveGameState();
        }, actualSaveInterval);
        console.log('✅ AUTO-SAVE: Started for new game');
      }
    }, 1000);
  }, [gameId, onDiscard, isGameActive, autoSaveInterval, detectDevicePerformance, saveGameState]);

  // Handle leave confirmation
  const handleLeaveConfirm = useCallback(() => {
    setShowLeaveModal(false);
    isNavigatingRef.current = true;
    window.history.back();
  }, []);

  // Handle leave cancel
  const handleLeaveCancel = useCallback(() => {
    setShowLeaveModal(false);
  }, []);

  // Handle exit game with save confirmation
  const handleExitGame = useCallback(() => {
    // Always show save confirmation when exiting via button
    setShowSaveExitModal(true);
  }, []);

  // Handle save and exit
  const handleSaveAndExit = useCallback(async () => {
    try {
      // Force save current state
      await saveGameState();
      
      // Wait a moment for save to complete
      setTimeout(() => {
        setShowSaveExitModal(false);
        isNavigatingRef.current = true;
        window.history.back();
      }, 500);
    } catch (error) {
      console.error('❌ ERROR: Failed to save before exit:', error);
      // Still allow exit but warn user
      setShowSaveExitModal(false);
      isNavigatingRef.current = true;
      window.history.back();
    }
  }, [saveGameState]);

  // Handle exit without saving
  const handleExitWithoutSaving = useCallback(() => {
    setShowSaveExitModal(false);
    isNavigatingRef.current = true;
    window.history.back();
  }, []);

  // Handle cancel save-and-exit
  const handleCancelSaveExit = useCallback(() => {
    setShowSaveExitModal(false);
  }, []);

  // Fullscreen controls
  const toggleFullscreen = useCallback(() => {
    if (!enableFullscreen) return;

    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
        fullscreenRef.current = true;
      }).catch(err => {
        console.error('Error entering fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
        fullscreenRef.current = false;
      }).catch(err => {
        console.error('Error exiting fullscreen:', err);
      });
    }
  }, [enableFullscreen]);

  // Mark changes as unsaved
  const markUnsaved = useCallback(() => {
    hasUnsavedChangesRef.current = true;
  }, []);

  // Mark changes as saved
  const markSaved = useCallback(() => {
    hasUnsavedChangesRef.current = false;
  }, []);



  // Setup auto-save interval with adaptive strategy
  useEffect(() => {
    if (!isGameActive || !gameId) {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current);
        autoSaveRef.current = null;
      }
      return;
    }

    // Determine save strategy based on device performance
    const performanceLevel = detectDevicePerformance();
    let actualSaveInterval = autoSaveInterval;
    
    if (performanceLevel === 'low' || performanceLevel === 'mobile') {
      actualSaveInterval = Math.max(autoSaveInterval * 2, 30000); // At least 30 seconds
      console.log(`⚡ PERFORMANCE: Using ${actualSaveInterval/1000}s save interval (single save strategy)`);
    }

    // Only do initial save if no existing save to resume from
    if (!showResumeModal) {
      console.log('💾 INITIAL SAVE: No existing save found, creating new save');
      saveGameState();
    } else {
      console.log('⏸️ INITIAL SAVE: Skipped - existing save will be resumed');
    }

    // Setup interval - simple save without cleanup (always overwrites)
    // Delay starting interval to allow resume process to complete
    const startInterval = () => {
      autoSaveRef.current = setInterval(() => {
        saveGameState(); // This automatically overwrites the single save
      }, actualSaveInterval);
    };
    
    if (showResumeModal) {
      // Wait for resume modal to be handled before starting auto-save
      console.log('⏸️ AUTO-SAVE: Waiting for resume decision before starting interval');
      setTimeout(startInterval, 2000); // Start after 2 seconds
    } else {
      // Start immediately if no resume needed
      startInterval();
    }

    return () => {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current);
        autoSaveRef.current = null;
      }
    };
  }, [isGameActive, gameId, saveGameState, autoSaveInterval, detectDevicePerformance]);

  // Setup navigation guards
  useEffect(() => {
    if (!isGameActive) return;

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isGameActive, handleBeforeUnload, handlePopState]);

  // Check for existing save on mount
  useEffect(() => {
    checkForExistingSave();
  }, [checkForExistingSave]);

  // Auto-save on critical game state changes
  useEffect(() => {
            // Don't trigger critical saves if resume modal is showing OR if we just resumed - wait for restoration to complete
        if (isGameActive && gameStateRef.current && !showResumeModal && !isResumingRef.current && !isCheckingSave) {
      markUnsaved();
      
      // Check for critical changes that require immediate save
      const currentState = gameStateRef.current;
      const lastSave = lastSaveStateRef.current;
      
      const stageChanged = currentState.currentStage !== lastSave?.stage;
      const teamsChanged = currentState.teams?.length !== lastSave?.teamsCount;
      const scoresChanged = JSON.stringify(currentState.teams?.map(t => t.score)) !== 
                           JSON.stringify(lastSave?.teamScores || []);
      
      const criticalChange = stageChanged || teamsChanged || scoresChanged;
      
      console.log('🔄 CHANGE DETECTION:', {
        stageChanged,
        teamsChanged, 
        scoresChanged,
        criticalChange,
        currentStage: currentState.currentStage,
        lastStage: lastSave?.stage,
        teamsCount: currentState.teams?.length,
        lastTeamsCount: lastSave?.teamsCount,
        resumeModalShowing: showResumeModal
      });
      
      if (criticalChange) {
        console.log('⚡ CRITICAL CHANGE: Triggering immediate save');
        // Debounced save for critical changes
        const timeoutId = setTimeout(() => {
          saveGameState();
        }, 1000); // Save after 1 second of no changes
        
        return () => clearTimeout(timeoutId);
      }
            } else if (showResumeModal) {
          console.log('⏸️ CRITICAL CHANGE: Skipped - resume modal is showing');
        } else if (isResumingRef.current) {
          console.log('⏸️ CRITICAL CHANGE: Skipped - resume in progress, waiting for restoration to complete');
        } else if (isCheckingSave) {
          console.log('⏸️ CRITICAL CHANGE: Skipped - checking for existing saves');
        }
  }, [isGameActive, markUnsaved, saveGameState, showResumeModal]);

  return {
    // State
    showResumeModal,
    showLeaveModal,
    showSaveExitModal,
    lastSaveTime,
    saveStatus,
    isFullscreen,
    
    // Actions
    handleResume,
    handleDiscard,
    handleLeaveConfirm,
    handleLeaveCancel,
    handleExitGame,
    handleSaveAndExit,
    handleExitWithoutSaving,
    handleCancelSaveExit,
    toggleFullscreen,
    markUnsaved,
    markSaved,
    saveGameState,
    
    // Utilities
    hasUnsavedChanges: hasUnsavedChangesRef.current,
    isNavigating: isNavigatingRef.current,
    cachedSaveData: cachedSaveRef.current
  };
};
